import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react';
import { useCurrentIssueDocuments } from 'components/dataProviders/withIssue';
import { useRetainedValue } from 'hooks/useRetainedValue';
import { useIssueStore } from 'store/issue';
import {
  buildReportIssueEstimateRows,
  ReportIssueEstimateRow,
} from 'shared/domain/report/estimate/reportIssueEstimateRow';
import {
  MultiIssueFilteredIssuesProvider,
  useMultiIssueFilteredIssues,
} from './multiIssueFilteredIssuesContext';
import { MultiIssueReportAvailabilityProvider } from './ReportSectionAvailabilityContext';
import { buildReportIssueStatsOverridesFromAttachmentCounts } from 'shared/domain/report/estimate/reportIssueStats';
import { ReportType } from 'shared/domain/report/types/model/common';
import { computeSingleIssueReportEstimate, SingleIssueReportEstimate } from '../model';
import type { InspectionPageEstimateSource } from 'shared/domain/report/estimate/inspection/inspectionPageEstimateSource';
import { useInspection } from 'store/inspection';
import { useInspectionIssues } from 'store/issue';

type ReportPageEstimateContextValue = {
  issueCount: number;
  issueRows: ReportIssueEstimateRow[];
  isLoading: boolean;
  kind: ReportType;
  inspectionSource: InspectionPageEstimateSource | null;
};

const defaultContextValue: ReportPageEstimateContextValue = {
  issueCount: 0,
  issueRows: [],
  isLoading: false,
  kind: ReportType.inspection,
  inspectionSource: null,
};

const ReportPageEstimateContext = createContext(defaultContextValue);

const NO_ESTIMATE: SingleIssueReportEstimate = { issueCount: 0, issueRows: [], isLoading: false };

export function useReportPageEstimateContext(): ReportPageEstimateContextValue {
  return useContext(ReportPageEstimateContext);
}

function ReportPageEstimateContextProvider({
  value,
  children,
}: {
  value: ReportPageEstimateContextValue;
  children: ReactNode;
}): ReactElement {
  return (
    <ReportPageEstimateContext.Provider value={value}>
      {children}
    </ReportPageEstimateContext.Provider>
  );
}

function MultiIssueReportPageEstimateProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { issueModels, attachmentCountsOfIssues, isLoading } = useMultiIssueFilteredIssues();

  const issueRows = useMemo(
    () =>
      buildReportIssueEstimateRows(
        issueModels,
        issueModels.map(({ _id }) => buildReportIssueStatsOverridesFromAttachmentCounts(attachmentCountsOfIssues[_id]))
      ),
    [issueModels, attachmentCountsOfIssues]
  );

  const value = useMemo(
    () => ({
      issueCount: issueModels.length,
      issueRows,
      isLoading,
      kind: ReportType.issueList,
      inspectionSource: null,
    }),
    [issueModels.length, issueRows, isLoading]
  );

  return (
    <ReportPageEstimateContextProvider value={value}>
      {children}
    </ReportPageEstimateContextProvider>
  );
}

export function SingleIssueReportPageEstimateProvider({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}): ReactElement {
  const issueModel = useIssueStore.use.currentIssue();
  const { documents } = useCurrentIssueDocuments();
  const estimate = useRetainedValue(
    useMemo(
      () => (open ? computeSingleIssueReportEstimate(issueModel, documents) : undefined),
      [open, issueModel, documents]
    )
  );

  const value = useMemo(
    () => ({
      ...(estimate ?? NO_ESTIMATE),
      kind: ReportType.singleIssue,
      inspectionSource: null,
    }),
    [estimate]
  );

  return (
    <ReportPageEstimateContextProvider value={value}>
      {children}
    </ReportPageEstimateContextProvider>
  );
}

export function InspectionReportPageEstimateProvider({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}): ReactElement {
  const {
    inspection,
    inspectionTemplateDocuments,
    inspectionDocuments,
    inspectionDocumentsLoading,
  } = useInspection();
  const { issues: issuesOnInspection, loading: issuesOnInspectionLoading } =
    useInspectionIssues();

  const inspectionSource = useMemo<InspectionPageEstimateSource | null>(() => {
    if (!open || !inspection) {
      return null;
    }

    return {
      inspection,
      inspectionTemplateDocuments,
      inspectionDocuments,
      issueRows: buildReportIssueEstimateRows(issuesOnInspection),
    };
  }, [
    open,
    inspection,
    inspectionTemplateDocuments,
    inspectionDocuments,
    issuesOnInspection,
  ]);

  const isLoading =
    open &&
    (!inspection ||
      inspectionDocumentsLoading ||
      issuesOnInspectionLoading);

  const value = useMemo(
    () => ({
      issueCount: issuesOnInspection.length,
      issueRows: inspectionSource?.issueRows ?? [],
      isLoading,
      kind: ReportType.inspection,
      inspectionSource,
    }),
    [inspectionSource, isLoading, issuesOnInspection.length]
  );

  return (
    <ReportPageEstimateContextProvider value={value}>
      {children}
    </ReportPageEstimateContextProvider>
  );
}

export function ReportFormEstimateShell({
  open,
  kind,
  children,
}: {
  open: boolean;
  kind: ReportType;
  children: ReactNode;
}): ReactElement {
  switch (kind) {
    case ReportType.issueList:
      return (
        <MultiIssueFilteredIssuesProvider open={open}>
          <MultiIssueReportAvailabilityProvider>
            <MultiIssueReportPageEstimateProvider>
              {children}
            </MultiIssueReportPageEstimateProvider>
          </MultiIssueReportAvailabilityProvider>
        </MultiIssueFilteredIssuesProvider>
      );
    case ReportType.singleIssue:
      return (
        <SingleIssueReportPageEstimateProvider open={open}>
          {children}
        </SingleIssueReportPageEstimateProvider>
      );
    case ReportType.inspection:
      return (
        <InspectionReportPageEstimateProvider open={open}>
          {children}
        </InspectionReportPageEstimateProvider>
      );
    default:
      return <>{children}</>;
  }
}
